'use client';
import React, { useState, useCallback } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Plus, Check, Loader2, FolderOpen, Info, X, ExternalLink } from 'lucide-react';
import { getAllPublishedArchives, Archive } from '@/lib/db/archives';

interface ArchiveSuggestion {
  archiveId: string | null;
  archiveTitle: string;
  confidence: number;
  reason: string;
  isNew: boolean;
}

interface NewArchiveRecommendation {
  suggestedTitle: string;
  suggestedDescription: string;
}

interface ArchiveSelectorProps {
  title: string;
  body: string;
  era: string;
  topic: string;
  region: string;
  onArchiveSelected: (archiveId: string | null, isNew: boolean, newTitle?: string) => void;
}

export default function ArchiveSelector({ title, body, era, topic, region, onArchiveSelected }: ArchiveSelectorProps) {
  const [expanded, setExpanded] = useState(false);
  const [archives, setArchives] = useState<Archive[]>([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [suggestions, setSuggestions] = useState<ArchiveSuggestion[]>([]);
  const [newArchiveRec, setNewArchiveRec] = useState<NewArchiveRecommendation | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedArchiveId, setSelectedArchiveId] = useState<string | null | 'new'>(null);
  const [newArchiveTitle, setNewArchiveTitle] = useState('');
  const [showAllArchives, setShowAllArchives] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsRun, setSuggestionsRun] = useState(false);

  const loadArchives = useCallback(async () => {
    if (archives.length > 0) return;
    setLoadingArchives(true);
    try {
      const data = await getAllPublishedArchives();
      setArchives(data);
    } catch {
      // silently fail — user can still manually browse
    } finally {
      setLoadingArchives(false);
    }
  }, [archives.length]);

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadArchives();
  };

  const getSuggestions = async () => {
    setLoadingSuggestions(true);
    setError(null);
    setSuggestionsRun(true);
    try {
      const res = await fetch('/api/ai/archive-suggestion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          body,
          era,
          topic,
          region,
          archives: archives.map((a) => ({
            id: a.id,
            title: a.title,
            description: a.description,
            eraId: a.eraId,
            region: a.region,
          })),
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSuggestions(data.suggestions || []);
      setNewArchiveRec(data.newArchiveRecommendation || null);
      if (data.newArchiveRecommendation?.suggestedTitle) {
        setNewArchiveTitle(data.newArchiveRecommendation.suggestedTitle);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to get suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectArchive = (archiveId: string | null | 'new') => {
    setSelectedArchiveId(archiveId);
    if (archiveId === 'new') {
      onArchiveSelected(null, true, newArchiveTitle);
    } else {
      onArchiveSelected(archiveId, false);
    }
  };

  const clearSelection = () => {
    setSelectedArchiveId(null);
    onArchiveSelected(null, false);
  };

  const confidenceColor = (c: number) =>
    c >= 75 ? '#2C5E3B' : c >= 50 ? '#D4A847' : '#5A5A5A';

  const confidenceLabel = (c: number) =>
    c >= 75 ? 'Strong match' : c >= 50 ? 'Good match' : 'Possible match';

  const selectedArchive = selectedArchiveId && selectedArchiveId !== 'new'
    ? archives.find((a) => a.id === selectedArchiveId)
    : null;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
    >
      {/* Header toggle */}
      <button
        type="button"
        onClick={handleExpand}
        className="w-full flex items-center justify-between px-6 py-4 transition-colors duration-150"
        style={{ color: 'var(--foreground)' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
      >
        <div className="flex items-center gap-2">
          <FolderOpen size={16} style={{ color: 'var(--primary)' }} />
          <h2 className="text-base font-semibold">Add to an Archive</h2>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
          >
            Optional
          </span>
          {selectedArchiveId && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: '#2C5E3B22', color: '#2C5E3B' }}
            >
              {selectedArchiveId === 'new' ? '+ New archive' : selectedArchive?.title || 'Selected'}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-5">
          {/* Explainer */}
          <div
            className="flex items-start gap-2 px-4 py-3 rounded-lg border text-xs"
            style={{ backgroundColor: '#00808011', borderColor: '#00808033', color: '#006060' }}
          >
            <Info size={13} className="flex-shrink-0 mt-0.5" />
            <span>
              <strong>Archives</strong> are curated collections that group multiple stories around a theme, era, or region.
              Adding your story to an archive helps it reach a wider audience and contributes to a richer body of work.
              You can skip this — your story will still be reviewed and published independently.{' '}
              <a
                href="/archives/browse"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 underline font-medium"
                style={{ color: '#006060' }}
              >
                Browse all archives <ExternalLink size={10} />
              </a>
            </span>
          </div>

          {/* AI Suggest button */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={getSuggestions}
              disabled={loadingSuggestions || loadingArchives}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 active:scale-95 disabled:opacity-60"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              {loadingSuggestions ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {loadingSuggestions ? 'Analysing story…' : suggestionsRun ? 'Re-analyse' : 'Suggest Archive with AI'}
            </button>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              AI reads your story and recommends the best archive
            </span>
          </div>

          {error && (
            <p className="text-xs" style={{ color: 'var(--status-rejected)' }}>
              {error}
            </p>
          )}

          {/* AI Suggestions */}
          {suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                AI SUGGESTIONS
              </p>
              {suggestions.map((s, idx) => (
                <button
                  key={`sug-${idx}`}
                  type="button"
                  onClick={() => s.isNew ? selectArchive('new') : selectArchive(s.archiveId)}
                  className="w-full text-left rounded-lg border p-4 transition-all duration-150"
                  style={{
                    borderColor: selectedArchiveId === (s.isNew ? 'new' : s.archiveId)
                      ? 'var(--primary)' :'var(--border)',
                    backgroundColor: selectedArchiveId === (s.isNew ? 'new' : s.archiveId)
                      ? '#E05A4710' :'var(--muted)',
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {s.isNew ? (
                          <Plus size={13} style={{ color: 'var(--primary)' }} />
                        ) : (
                          <FolderOpen size={13} style={{ color: confidenceColor(s.confidence) }} />
                        )}
                        <span className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                          {s.isNew ? `Start new: "${s.archiveTitle}"` : s.archiveTitle}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                        {s.reason}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span
                        className="text-xs font-mono font-semibold"
                        style={{ color: confidenceColor(s.confidence), fontFamily: 'var(--font-mono)' }}
                      >
                        {s.confidence}%
                      </span>
                      <span className="text-xs" style={{ color: confidenceColor(s.confidence) }}>
                        {confidenceLabel(s.confidence)}
                      </span>
                      {selectedArchiveId === (s.isNew ? 'new' : s.archiveId) && (
                        <Check size={14} style={{ color: 'var(--primary)' }} />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* New archive name input */}
          {selectedArchiveId === 'new' && (
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                New Archive Title
              </label>
              <input
                type="text"
                value={newArchiveTitle}
                onChange={(e) => {
                  setNewArchiveTitle(e.target.value);
                  onArchiveSelected(null, true, e.target.value);
                }}
                placeholder="e.g. Women in the Liberation Struggle"
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
                onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
              />
              {newArchiveRec?.suggestedDescription && (
                <p className="text-xs mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Suggested description: {newArchiveRec.suggestedDescription}
                </p>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>or browse manually</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
          </div>

          {/* Manual archive list */}
          {loadingArchives ? (
            <div className="flex items-center gap-2 py-4 justify-center">
              <Loader2 size={16} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Loading archives…</span>
            </div>
          ) : archives.length === 0 ? (
            <div
              className="rounded-lg border border-dashed flex flex-col items-center justify-center py-6"
              style={{ borderColor: 'var(--border)' }}
            >
              <FolderOpen size={22} style={{ color: 'var(--muted-foreground)' }} className="mb-2" />
              <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No archives yet</p>
              <p className="text-xs mt-1 text-center max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
                Be the first to start an archive. Click "Suggest Archive with AI" above or enter a title below.
              </p>
              <button
                type="button"
                onClick={() => selectArchive('new')}
                className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-150"
                style={{ borderColor: 'var(--primary)', color: 'var(--primary)', backgroundColor: '#E05A4710' }}
              >
                <Plus size={12} />
                Start a new archive
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {(showAllArchives ? archives : archives.slice(0, 4)).map((archive) => (
                <button
                  key={archive.id}
                  type="button"
                  onClick={() => selectArchive(archive.id)}
                  className="w-full text-left rounded-lg border p-3 transition-all duration-150"
                  style={{
                    borderColor: selectedArchiveId === archive.id ? 'var(--primary)' : 'var(--border)',
                    backgroundColor: selectedArchiveId === archive.id ? '#E05A4710' : 'var(--muted)',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
                        {archive.title}
                      </p>
                      <p className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--muted-foreground)' }}>
                        {archive.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        {archive.contributionCount} stories
                      </span>
                      {selectedArchiveId === archive.id && (
                        <Check size={14} style={{ color: 'var(--primary)' }} />
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {archives.length > 4 && (
                <button
                  type="button"
                  onClick={() => setShowAllArchives(!showAllArchives)}
                  className="text-xs font-medium transition-colors duration-150"
                  style={{ color: 'var(--primary)' }}
                >
                  {showAllArchives ? 'Show less' : `Show all ${archives.length} archives`}
                </button>
              )}

              <button
                type="button"
                onClick={() => selectArchive('new')}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed text-sm font-medium transition-all duration-150"
                style={{
                  borderColor: selectedArchiveId === 'new' ? 'var(--primary)' : 'var(--border)',
                  color: selectedArchiveId === 'new' ? 'var(--primary)' : 'var(--muted-foreground)',
                  backgroundColor: selectedArchiveId === 'new' ? '#E05A4710' : 'transparent',
                }}
              >
                <Plus size={14} />
                Start a new archive
                {selectedArchiveId === 'new' && <Check size={14} style={{ color: 'var(--primary)' }} className="ml-auto" />}
              </button>
            </div>
          )}

          {/* Clear selection */}
          {selectedArchiveId && (
            <button
              type="button"
              onClick={clearSelection}
              className="flex items-center gap-1.5 text-xs transition-colors duration-150"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <X size={12} />
              Clear selection — submit without an archive
            </button>
          )}
        </div>
      )}
    </div>
  );
}
